import React from "react"

import {default as ItemType, LockingSystemZone} from "../types/LockingSystemZone"
import {LockingSystemZoneApi, LockingSystemZoneApi as ItemApi} from "../services/LockingSystemZoneApi"
import {MessageInterface} from "../components/common/Message"
import SelectOption from "@customTypes/SelectOptions/LockingSystemZoneSelectOption";

export type LockingSystemZoneContextType = {
    addItem:    (item: ItemType) => void;
    deleteItem: (id: number) => void;
    editItem:   (item: ItemType) => void;

    fetchItem:  (id: number, include?: string) => void;
    fetchItems: (search?: string, force?: boolean) => void;

    fetchSelectOptions: (force?: boolean) => void
    selectOptions: SelectOption[] | undefined

    items:      ItemType[] | undefined;

    loadingItems: boolean;

    message:      MessageInterface | undefined
    closeMessage: () => void
}

const initialState = {
    addItem:      () => {},
    deleteItem:   () => {},
    editItem:     () => {},
    fetchItem:    () => {},
    fetchItems:   () => {},
    fetchSelectOptions: () => {},
    selectOptions: undefined,
    items:        undefined,
    loadingItems: true,
    message:      undefined,
    closeMessage: () => {},
}

export const LockingSystemZoneContext = React.createContext<LockingSystemZoneContextType>(initialState)

interface providerProps {
    children: React.ReactNode
}

const LockingSystemZoneProvider: React.FC<providerProps> = ({ children }) => {
    const [
        items, setItems
    ] = React.useState<ItemType[] | undefined>(initialState.items)
    const [loadingItems, setLoadingItems] = React.useState<boolean>(initialState.loadingItems)
    const [message, setMessage] = React.useState<MessageInterface | undefined>(initialState.message)
    const [selectOptions, setSelectOptions] = React.useState<SelectOption[] | undefined>(initialState.selectOptions)

    const addItem = async (newItem: ItemType): Promise<ItemType> => {
        const newZone = await LockingSystemZoneApi.add(newItem, () => {})

        if (items && newZone) {
            setItems([newItem, ...items]);
        }

        return newZone
    }

    const deleteItem = (id: number) => {
        if (items === undefined) return

        ItemApi.delete(id);

        const newItems = [...items];
        newItems.splice(newItems.findIndex(i => i.id === id), 1);

        setItems(newItems);
    }

    const editItem = async (editedItem: ItemType): Promise<void> => {
        if (items === undefined) return;

        const result = await ItemApi.edit(editedItem);

        const newItems: ItemType[] = items.map(oldItem => {
            if (oldItem.id === editedItem.id && result.id) {
                return {
                    ...oldItem,
                    name: result.name,
                    description: result.description,
                }
            } else {
                return oldItem
            }
        })

        setItems(newItems)
    }

    const fetchItem = async (id: number, include?: string): Promise<LockingSystemZone | void> => {
        setLoadingItems(true)

        const result = await LockingSystemZoneApi.get(id, include)
            .catch((e) => setErrorMessage(e))

        setLoadingItems(false)

        return result
    }

    const fetchItems = async (search?: string, force: boolean = false) => {
        if (items === undefined || force) {
            setLoadingItems(true)

            const result = await ItemApi
                .getAll(search)
                .catch((e) => {
                    setMessage({
                        text: e.response.data.message || '?',
                        type: 'error'
                    })

                    setLoadingItems(false)
                })

            if (result) {
                setItems(result)

                setLoadingItems(false)
            }
        }
    }

    const fetchSelectOptions = async (force: boolean = false): Promise<SelectOption[] | void> => {

        if (undefined === selectOptions || force) {
            setLoadingItems(true)

            const result = await LockingSystemZoneApi.getSelectOptions()
                .catch((e) => setErrorMessage(e))

            if (result) {
                setSelectOptions(result)

                setLoadingItems(false)
            }
        }
    }

    const closeMessage = () => setMessage(undefined)

    const setErrorMessage = (e: any) => {
        setMessage({
            text: e.response.data.message || '?',
            type: 'error'
        })

        //setInProgress(false)
        setLoadingItems(false)
    }

    return (
        <LockingSystemZoneContext.Provider
            value={{
                addItem,
                deleteItem,
                editItem,
                fetchItem,
                fetchItems,
                fetchSelectOptions,
                selectOptions,
                items,
                loadingItems,
                message,
                closeMessage,
            }}
        >
            {children}
        </LockingSystemZoneContext.Provider>
    );
}

export const useLockingSystemZones = () => {
    return React.useContext(LockingSystemZoneContext) as LockingSystemZoneContextType
}

export default LockingSystemZoneProvider